import { lazy, Suspense, useEffect } from 'react';

import config from 'config';
import { STARGATE_ROUTES } from 'config/application-routes';
import { BUSINESS_UNITS } from 'core';
import { ErrorBoundary, FallbackProps } from 'react-error-boundary';
import { Outlet, Route, Routes, useLocation } from 'react-router';
import { Error401, Error403, Error500, ErrorGeneric, ErrorProcess, Loader } from 'ui/components';
import PageBuilder from 'ui/pages/PageBuilder';
import { PageCategoryBreadCrumb } from 'ui/pages/PageCategoryBreadCrumb';

const Header = lazy(() =>
  import(/* webpackChunkName: "header" */ 'ui/containers/SectionBuilder/sections/Header').then(
    (module) => ({ default: module.Header }),
  ),
);

const PageCheckout = lazy(
  () => import(/* webpackChunkName: "page-checkout" */ '../pages/PageCheckout'),
);

const PageOrderSuccess = lazy(
  () => import(/* webpackChunkName: "page-success" */ '../pages/PageOrderSuccess'),
);

const PageGifts = lazy(() => import(/* webpackChunkName: "page-gifts" */ '../pages/PageGifts'));

const PageSearch = lazy(() => import(/* webpackChunkName: "page-search" */ '../pages/PageSearch'));

const PageProductsByTag = lazy(
  () => import(/* webpackChunkName: "page-products-by-tag-v2" */ '../pages/PageProductsByTag'),
);

const PageEmptyRecommendation = lazy(
  () =>
    import(/* webpackChunkName: "page-empty-recommendation" */ '../pages/PageEmptyRecommendation'),
);

const ExtendedCaptationPage = lazy(
  () => import(/* webpackChunkName: "page-extended-captation" */ '../pages/ExtendedCaptation'),
);

const Login = lazy(() => import(/* webpackChunkName: "login" */ '../pages/Login'));

const Logout = lazy(() => import(/* webpackChunkName: "logout" */ '../pages/Logout'));

const Faq = lazy(() => import(/* webpackChunkName: "faq" */ '../pages/FAQ'));

const ErrorVDF = lazy(() => import(/* webpackChunkName: "page-error" */ '../pages/ErrorVDF'));

const PageTapume = lazy(() => import(/* webpackChunkName: "page-tapume" */ '../pages/PageTapume'));

const PageCategory = lazy(
  () => import(/* webpackChunkName: "page-category" */ '../pages/PageCategory'),
);

const PageProductDetail = lazy(
  () => import(/* webpackChunkName: "page-product-detail" */ '../pages/PageProductDetail'),
);

const RecoverPassword = lazy(
  () => import(/* webpackChunkName: "recover-password" */ '../pages/RecoverPassword'),
);

const RecoverPasswordEmail = lazy(
  () => import(/* webpackChunkName: "recover-email" */ '../pages/RecoverPassword/Email'),
);

const RecoverPasswordEmailSuccess = lazy(
  () =>
    import(
      /* webpackChunkName: "recover-email-success" */ '../pages/RecoverPassword/Email/RecoverySuccess'
    ),
);

const PageDepartments = lazy(
  () => import(/* webpackChunkName: "page-departments" */ '../pages/Departments/PageDepartments'),
);

const ResaleIntentionPage = lazy(
  () => import(/* webpackChunkName: "resale-intention" */ '../pages/ResaleIntentionPage'),
);

const PageCart = lazy(() => import(/* webpackChunkName: "cart-page" */ '../pages/Cart/CartPage'));

const Router = () => {
  const location = useLocation();

  const ErrorFallback = ({ error }: FallbackProps) => {
    useEffect(() => {
      dataLayer.push({
        'event-action': `callback:${location.pathname}`,
        'event-category': `${config.app.short}-portal:${location.pathname}`,
        'event-label': error,
      });

      window?.newrelic?.noticeError(error, { 'Application Error': error });
    }, []);

    return <ErrorGeneric />;
  };

  return (
    <Suspense fallback={<Loader />}>
      <Routes>
        <Route
          element={
            <ErrorBoundary FallbackComponent={ErrorFallback}>
              <>
                {config.app.short === BUSINESS_UNITS.VDI ? <Header /> : null}
                <Outlet />
              </>
            </ErrorBoundary>
          }
        >
          <Route path={STARGATE_ROUTES.LOGIN} element={<Login />} />
          <Route path={STARGATE_ROUTES.RESALE_INTENTION} element={<ResaleIntentionPage />} />
          <Route path={STARGATE_ROUTES.EXTENDED_CAPTATION} element={<ExtendedCaptationPage />} />
          <Route path={STARGATE_ROUTES.ERROR_500} element={<Error500 />} />
          <Route path={STARGATE_ROUTES.ERROR_401} element={<Error401 />} />
          <Route path={STARGATE_ROUTES.ERROR_403} element={<Error403 />} />
          <Route path={STARGATE_ROUTES.ERROR_PROCESS} element={<ErrorProcess />} />
          <Route path={STARGATE_ROUTES.ERROR_COMBO} element={<ErrorGeneric />} />
          <Route path={STARGATE_ROUTES.ERROR_GENERIC} element={<ErrorGeneric />} />
          <Route path={STARGATE_ROUTES.ERROR_VDF} element={<ErrorVDF />} />
          <Route path={STARGATE_ROUTES.FAQ} element={<Faq />} />
          <Route path={STARGATE_ROUTES.LOGOUT} element={<Logout />} />
          <Route path={STARGATE_ROUTES.GIFTS} element={<PageGifts />} />
          <Route path={STARGATE_ROUTES.PRODUCTS_BY_TAG} element={<PageProductsByTag />} />
          <Route
            path={STARGATE_ROUTES.EMPTY_RECOMMENDATION}
            element={<PageEmptyRecommendation />}
          />
          <Route path={STARGATE_ROUTES.TAPUME} element={<PageTapume />} />
          <Route path={STARGATE_ROUTES.CATEGORIES} element={<PageCategory />} />
          <Route path={STARGATE_ROUTES.COMBO} element={<></>} />

          <Route path={STARGATE_ROUTES.CHECKOUT_CONFIRMATION} element={<PageOrderSuccess />} />
          <Route path={STARGATE_ROUTES.CHECKOUT} element={<PageCheckout />} />
          <Route path={STARGATE_ROUTES.CART} element={<PageCart />} />

          <Route path={STARGATE_ROUTES.DEPARTMENT}>
            <Route index element={<PageDepartments />} />
            <Route
              path={STARGATE_ROUTES.DEPARTMENTS}
              element={
                <PageBuilder
                  documentType="page-categories"
                  route={`/${STARGATE_ROUTES.DEPARTMENT}/${STARGATE_ROUTES.DEPARTMENTS}`}
                  name="Departamento"
                >
                  <PageCategoryBreadCrumb />
                </PageBuilder>
              }
            />
          </Route>
          <Route path={STARGATE_ROUTES.SEARCH} element={<PageSearch />} />
          <Route path={STARGATE_ROUTES.PRODUCT_DETAIL} element={<PageProductDetail />} />

          <Route path={STARGATE_ROUTES.RECOVER_PASSWORD}>
            <Route index element={<RecoverPassword />} />
            <Route path={STARGATE_ROUTES.RECOVER_PASSWORD_EMAIL}>
              <Route index element={<RecoverPasswordEmail />} />
              <Route
                path={STARGATE_ROUTES.RECOVER_PASSWORD_EMAIL_SUCCESS}
                element={<RecoverPasswordEmailSuccess />}
              />
            </Route>
          </Route>
        </Route>
        {config.app.short === BUSINESS_UNITS.EUD.toLowerCase() && (
          <Route path="*" index element={<Login />} />
        )}
      </Routes>
    </Suspense>
  );
};

export default Router;
